import React, { useEffect, useState, useCallback } from "react";
import { KeyRound, RefreshCw, Check, X, Copy, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  listPasswordResetRequests,
  approvePasswordResetRequest,
  denyPasswordResetRequest,
} from "../../lib/api";

// Architect-only · Password Reset Inbox (v18.99.9)
// Lists pending reset requests and lets the architect approve/deny.
// On approval, the cleartext temp password is shown ONCE — architect
// delivers it out-of-band (text/voice/in-person).
export default function PasswordResetInbox() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tempPwModal, setTempPwModal] = useState(null); // {email, temp_password}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPasswordResetRequests("pending");
      setRequests(res?.requests || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    try {
      const res = await approvePasswordResetRequest(req.id);
      setTempPwModal({
        email: res.target_email,
        temp_password: res.temp_password,
        note: res.note,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const deny = async (req) => {
    if (!window.confirm(`Deny password reset request from ${req.user_email}?`)) return;
    try {
      await denyPasswordResetRequest(req.id);
      toast.success("Request denied.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deny failed");
    }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(tempPwModal.temp_password);
      toast.success("Temp password copied to clipboard.");
    } catch {
      toast.error("Copy failed — select the text manually.");
    }
  };

  return (
    <section
      data-testid="password-reset-inbox"
      className="border border-amber-500/30 bg-[#0b0b0d] p-5 sm:p-6 mt-6"
    >
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-300">
            <KeyRound className="h-3 w-3" />
            Password reset inbox
          </div>
          <h3 className="font-heading text-lg sm:text-xl text-zinc-100 mt-1 tracking-[0.05em]">
            Locked-out users awaiting Architect approval
          </h3>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="reset-inbox-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-300 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="text-rose-400 text-xs border border-rose-500/30 bg-rose-500/5 p-3 mb-3 font-mono">
          {String(error)}
        </div>
      )}

      {!loading && requests.length === 0 && !error && (
        <div
          className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] py-6 text-center border border-dashed border-zinc-800"
          data-testid="reset-inbox-empty"
        >
          No pending reset requests.
        </div>
      )}

      {requests.length > 0 && (
        <ul className="space-y-2" data-testid="reset-inbox-list">
          {requests.map((req) => (
            <li
              key={req.id}
              data-testid={`reset-request-${req.id}`}
              className="border border-zinc-800 bg-zinc-950/60 p-3 flex flex-wrap items-start gap-3 justify-between"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">
                  <span className="text-zinc-500">@{req.user_handle || "(no handle)"} · </span>
                  <span className="font-mono">{req.user_email}</span>
                </div>
                {req.reason && (
                  <div className="text-[11px] text-zinc-400 mt-1 italic">
                    “{req.reason}”
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1 font-mono">
                  filed {req.requested_at ? new Date(req.requested_at).toISOString().slice(0, 19).replace("T", " ") : "—"}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => approve(req)}
                  data-testid={`approve-reset-${req.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
                >
                  <Check className="h-3.5 w-3.5" /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => deny(req)}
                  data-testid={`deny-reset-${req.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-rose-500/60 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 transition-colors"
                >
                  <X className="h-3.5 w-3.5" /> Deny
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tempPwModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="temp-password-modal"
          onClick={() => setTempPwModal(null)}
        >
          <div
            className="border border-amber-500 bg-[#0b0b0d] p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-2">
              <AlertTriangle className="h-3 w-3" /> Shown once · deliver out-of-band
            </div>
            <h4 className="font-heading text-xl text-zinc-100 mb-3">
              Temp password for {tempPwModal.email}
            </h4>
            <div className="font-mono text-lg tracking-[0.15em] text-emerald-300 border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 mb-3 select-all break-all">
              {tempPwModal.temp_password}
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed mb-4">
              {tempPwModal.note}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyTemp}
                data-testid="copy-temp-password"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                type="button"
                onClick={() => setTempPwModal(null)}
                data-testid="close-temp-password"
                className="flex-1 px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-300 transition-colors"
              >
                I've delivered it — close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && requests.length === 0 && (
        <div className="text-zinc-500 text-[11px] uppercase tracking-[0.25em] py-6 text-center">
          <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
          Loading inbox…
        </div>
      )}
    </section>
  );
}
